import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import { fmtDate, fmtMoney, fmtNumber } from '../../lib/format';

interface PortalQuote {
  version: number;
  title: string | null;
  department: string | null;
  projectCode: string;
  projectName: string;
  items: { name: string; qty: number; unitPrice: number; total: number; note: string | null }[];
  customer: { name?: string; legalName?: string; ico?: string; street?: string; city?: string; zip?: string } | null;
  currency: string;
  base: number;
  vatRatePct: number;
  vat: number;
  total: number;
  issuedAt: string;
  validUntil: string | null;
  notes: string | null;
  status: string;
  seen: string;
  decidedAt: string | null;
  decidedBy: string | null;
  decisionNote: string | null;
  acceptedAt: string | null;
  billing: {
    number: string;
    issuedAt: string;
    dueAt: string | null;
    sent: boolean;
    sentAt: string | null;
    paid: boolean;
    paidAt: string | null;
    currency: string;
    total: number;
  } | null;
  supplier: { name: string; email: string | null; phone: string | null; web: string | null };
}

// Průběh zakázky pro klienta: přijetí → faktura → odeslání → zaplacení.
// Je to jediné místo, kde klient bez telefonování vidí, kde se věci nacházejí.
function Progress({ data }: { data: PortalQuote }) {
  const b = data.billing;
  const steps: { label: string; done: boolean; date: string | null; detail?: string }[] = [
    { label: 'Nabídka přijata', done: true, date: data.decidedAt ?? data.acceptedAt },
    { label: 'Faktura vystavena', done: !!b, date: b?.issuedAt ?? null, detail: b?.number },
    { label: 'Faktura odeslána', done: !!b?.sent, date: b?.sentAt ?? null },
    {
      label: 'Zaplaceno',
      done: !!b?.paid,
      date: b?.paidAt ?? null,
      detail: b && !b.paid && b.dueAt ? `splatnost ${fmtDate(b.dueAt)}` : undefined,
    },
  ];
  // Aktuální krok = první nehotový; zvýrazníme ho, ať je jasné, na čem se čeká.
  const current = steps.findIndex((s) => !s.done);

  return (
    <div className="mb-5 rounded-xl border border-zinc-200 p-4">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">Průběh zakázky</h2>
        {b && <span className="text-sm tabular-nums text-zinc-500">{fmtMoney(b.total, b.currency)} s DPH</span>}
      </div>
      <ol className="space-y-2.5">
        {steps.map((st, i) => (
          <li key={st.label} className="flex items-start gap-3">
            <span
              aria-hidden
              className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full text-[11px] font-bold ${
                st.done
                  ? 'bg-emerald-600 text-white'
                  : i === current
                    ? 'border-2 border-amber-500 text-amber-600'
                    : 'border border-zinc-300 text-zinc-300'
              }`}
            >
              {st.done ? '✓' : i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <p className={`text-sm ${st.done ? 'font-medium' : i === current ? 'font-medium text-amber-700' : 'text-zinc-400'}`}>
                {st.label}
                {i === current && <span className="ml-2 text-xs font-normal text-amber-600">čeká se</span>}
              </p>
              {(st.date || st.detail) && (
                <p className="text-xs text-zinc-500">
                  {[st.detail, st.date ? fmtDate(st.date) : null].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

const token = window.location.pathname.replace(/^\/nabidka\//, '').replace(/\/$/, '');

// Podpisové pole — kreslení prstem/myší, vrací PNG data URL.
function SignaturePad({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    c.width = c.offsetWidth * 2;
    c.height = 150 * 2;
    const ctx = c.getContext('2d')!;
    ctx.scale(2, 2);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1c1c1c';
    let drawing = false;
    const pos = (e: PointerEvent) => {
      const r = c.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const down = (e: PointerEvent) => { drawing = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); c.setPointerCapture(e.pointerId); };
    const move = (e: PointerEvent) => {
      if (!drawing) return;
      const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke();
      if (!dirty) setDirty(true);
      onChange(c.toDataURL('image/png'));
      e.preventDefault();
    };
    const up = () => { drawing = false; };
    c.addEventListener('pointerdown', down);
    c.addEventListener('pointermove', move);
    c.addEventListener('pointerup', up);
    return () => { c.removeEventListener('pointerdown', down); c.removeEventListener('pointermove', move); c.removeEventListener('pointerup', up); };
  }, [dirty, onChange]);

  const clear = () => {
    const c = ref.current;
    if (!c) return;
    c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    setDirty(false);
    onChange(null);
  };

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-zinc-400">Podpis (nepovinný)</span>
        {dirty && <button type="button" onClick={clear} className="text-xs text-zinc-500 hover:underline">smazat</button>}
      </div>
      <canvas ref={ref} className="h-[150px] w-full touch-none rounded-xl border border-dashed border-zinc-300 bg-white" aria-label="Podpisové pole" />
    </div>
  );
}

export default function QuoteApprovalPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['portal-quote', token],
    queryFn: () => api.get<PortalQuote>(`/api/portal/quote/${token}`),
    retry: false,
  });

  const [mode, setMode] = useState<'accepted' | 'rejected' | null>(null);
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [signature, setSignature] = useState<string | null>(null);

  const decide = useMutation({
    meta: { quiet: true },
    mutationFn: (body: { decision: 'accepted' | 'rejected'; name: string; note?: string; signature?: string }) =>
      api.post(`/api/portal/quote/${token}/decision`, { ...body, seen: data?.seen }),
    // I po odmítnutí: když se nabídka změnila, klient hned uvidí aktuální verzi.
    onSettled: () => qc.invalidateQueries({ queryKey: ['portal-quote', token] }),
  });

  if (isLoading) return <Shell><p className="text-center text-sm text-zinc-500">Načítám nabídku…</p></Shell>;
  if (error) {
    return (
      <Shell>
        <div className="py-10 text-center">
          <p className="text-lg font-medium">Odkaz je neplatný nebo vypršel</p>
          <p className="mt-1 text-sm text-zinc-500">Požádejte prosím o nový odkaz.</p>
        </div>
      </Shell>
    );
  }
  if (!data) return null;

  const accepted = data.status === 'accepted';
  // Přijetí mohlo potvrdit i studio — pak decidedAt chybí, ale nabídka je přijatá.
  const decided = !!data.decidedAt || accepted;

  return (
    <Shell>
      {/* Hlavička */}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Cenová nabídka</h1>
          <p className="mt-0.5 text-sm text-zinc-500">
            {data.title || `${data.projectCode} — ${data.projectName}`}{data.department ? ` · ${data.department}` : ''} · verze {data.version}
          </p>
        </div>
        <a
          href={`/api/portal/quote/${token}/pdf`}
          target="_blank"
          rel="noreferrer"
          className="rounded-lg border border-zinc-300 px-3 py-2 text-sm font-medium hover:bg-zinc-50"
        >
          ⬇ Stáhnout PDF
        </a>
      </div>

      {/* Výsledek, pokud je rozhodnuto */}
      {decided && (
        <div className={`mb-5 rounded-xl border p-4 ${accepted ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`}>
          <p className="font-medium">{accepted ? '✅ Nabídka je přijatá' : '❌ Nabídku jste odmítli'}</p>
          {(data.decidedBy || data.decidedAt) && (
            <p className="mt-0.5 text-sm text-zinc-600">
              {[data.decidedBy, data.decidedAt ? fmtDate(data.decidedAt) : null].filter(Boolean).join(' · ')}
              {data.decisionNote ? ` · „${data.decisionNote}"` : ''}
            </p>
          )}
          {!accepted && <p className="mt-2 text-sm text-zinc-500">Děkujeme za odpověď.</p>}
        </div>
      )}

      {accepted && <Progress data={data} />}

      {/* Položky */}
      <div className="overflow-hidden rounded-xl border border-zinc-200">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-left text-[11px] uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="px-3 py-2 font-medium">Položka</th>
              <th className="hidden px-3 py-2 text-right font-medium sm:table-cell">Ks</th>
              <th className="hidden px-3 py-2 text-right font-medium sm:table-cell">Cena/ks</th>
              <th className="px-3 py-2 text-right font-medium">Celkem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {data.items.map((it, i) => (
              <tr key={i}>
                <td className="px-3 py-2">
                  {it.name}
                  {it.note && <span className="block text-xs text-zinc-400">{it.note}</span>}
                  <span className="block text-xs tabular-nums text-zinc-500 sm:hidden">{fmtNumber(it.qty)} × {fmtMoney(it.unitPrice, data.currency)}</span>
                </td>
                <td className="hidden px-3 py-2 text-right tabular-nums text-zinc-500 sm:table-cell">{fmtNumber(it.qty)}</td>
                <td className="hidden px-3 py-2 text-right tabular-nums text-zinc-500 sm:table-cell">{fmtMoney(it.unitPrice, data.currency)}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums font-medium">{fmtMoney(it.total, data.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Součty */}
      <div className="mt-4 flex justify-end">
        <div className="w-64 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-zinc-500">Základ</span><span className="tabular-nums">{fmtMoney(data.base, data.currency)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">DPH {data.vatRatePct} %</span><span className="tabular-nums">{fmtMoney(data.vat, data.currency)}</span></div>
          <div className="flex justify-between border-t border-zinc-200 pt-1 text-base font-semibold">
            <span>Celkem</span><span className="tabular-nums">{fmtMoney(data.total, data.currency)}</span>
          </div>
        </div>
      </div>

      {data.validUntil && (
        <p className="mt-3 text-sm text-zinc-500">Platnost nabídky do {fmtDate(data.validUntil)}.</p>
      )}
      {data.notes && (
        <div className="mt-4 rounded-xl bg-zinc-50 p-4 text-sm whitespace-pre-line text-zinc-700">{data.notes}</div>
      )}

      {/* Rozhodnutí */}
      {!decided && (
        <div className="mt-6 rounded-xl border border-zinc-200 p-4">
          {!mode ? (
            <>
              <p className="mb-3 text-sm font-medium">Jak chcete s nabídkou naložit?</p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  onClick={() => setMode('accepted')}
                  className="flex-1 rounded-lg bg-emerald-600 px-4 py-3 font-semibold text-white hover:bg-emerald-700"
                >
                  ✓ Přijmout nabídku
                </button>
                <button
                  onClick={() => setMode('rejected')}
                  className="flex-1 rounded-lg border border-zinc-300 px-4 py-3 font-medium hover:bg-zinc-50"
                >
                  Odmítnout
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <p className="text-sm font-medium">
                {mode === 'accepted' ? '✓ Přijetí nabídky' : 'Odmítnutí nabídky'}
                <button onClick={() => setMode(null)} className="ml-2 text-xs font-normal text-zinc-500 hover:underline">změnit</button>
              </p>

              <div>
                <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.08em] text-zinc-400">Vaše jméno *</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jméno a příjmení"
                  className="h-11 w-full rounded-lg border border-zinc-300 bg-white px-3 text-base text-zinc-900 outline-none focus:border-zinc-900"
                />
              </div>

              <div>
                <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.08em] text-zinc-400">
                  Poznámka {mode === 'rejected' ? '(důvod)' : '(nepovinná)'}
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 outline-none focus:border-zinc-900"
                />
              </div>

              {mode === 'accepted' && <SignaturePad onChange={setSignature} />}

              {decide.error && (
                <p className="text-sm text-red-600">{(decide.error as ApiError).message}</p>
              )}

              <button
                onClick={() =>
                  decide.mutate({
                    decision: mode,
                    name: name.trim(),
                    note: note.trim() || undefined,
                    signature: signature ?? undefined,
                  })
                }
                disabled={decide.isPending || name.trim().length < 2}
                className={`h-12 w-full rounded-lg font-semibold text-white disabled:opacity-50 ${
                  mode === 'accepted' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-zinc-900 hover:bg-zinc-800'
                }`}
              >
                {decide.isPending
                  ? 'Odesílám…'
                  : mode === 'accepted'
                    ? '✓ Závazně přijmout nabídku'
                    : 'Odmítnout nabídku'}
              </button>
              <p className="text-center text-xs text-zinc-400">
                Zaznamenáme datum, čas a vaše jméno jako potvrzení tohoto rozhodnutí.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Patička dodavatele */}
      <div className="mt-8 border-t border-zinc-200 pt-4 text-center text-xs text-zinc-400">
        {[data.supplier.name, data.supplier.email, data.supplier.phone].filter(Boolean).join(' · ')}
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-zinc-100 px-4 py-8 text-zinc-900">
      <div className="mx-auto max-w-2xl rounded-2xl bg-white p-5 shadow-sm sm:p-7">{children}</div>
    </div>
  );
}
